import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Tabs from '@radix-ui/react-tabs';
import { CreditCard, GraduationCap, Pencil, Percent, Plus, Search, Trash2, Wallet } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { AppError } from '@/core/errors/AppError';
import { useSession } from '@/core/auth/useSession';
import { useCrearReglaDescuento, useDesactivarReglaDescuento, useEditarReglaDescuento, useReglasDescuento, } from '@/data/hooks/useDescuentosReglas';
import { DescuentoReglaFormSchema, TIPOS_CLASE_DISPONIBLES, TIPOS_DESCUENTO, } from '@/data/dto/descuentoRegla';
import { formatearFecha, formatearMonto } from '@/core/utils/formatters';
// Claves del catalogo cmetodopago en el backend (no confundir con la constante
// METODOS_PAGO de reserva.ts, que es un subset pensado para "metodo previsto").
const METODOS_PAGO_RESTRICCION = [
    'EFECTIVO',
    'TARJETA_CREDITO',
    'TARJETA_DEBITO',
    'TRANSFERENCIA',
    'OTRO',
];
import { Button } from '@/shared/components/Button';
import { Chip } from '@/shared/components/Chip';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { Field } from '@/shared/components/Field';
import { Input } from '@/shared/components/Input';
import { MoneyInput } from '@/shared/components/MoneyInput';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, } from '@/shared/components/Modal';
import { Select } from '@/shared/components/Select';
import { Table } from '@/shared/components/Table';
import { Textarea } from '@/shared/components/Textarea';
import { useToast } from '@/shared/components/Toast';
const TIPO_LABEL = {
    BANCO: 'Banco',
    ESCUELA: 'Escuela',
    PROMOCION: 'Promoción',
    OTRO: 'Otro',
};
const TIPO_TONO = {
    BANCO: 'info',
    ESCUELA: 'primary',
    PROMOCION: 'success',
    OTRO: 'neutral',
};
const TIPO_CLASE_LABEL = {
    PRIVADA: 'Privada',
    GRUPAL: 'Grupal',
    EMPRESA: 'Empresa',
};
const METODO_PAGO_LABEL = {
    EFECTIVO: 'Efectivo',
    TARJETA_CREDITO: 'Tarjeta crédito',
    TARJETA_DEBITO: 'Tarjeta débito',
    TRANSFERENCIA: 'Transferencia',
    OTRO: 'Otro',
};
const ESTADO_LABEL = {
    activa: 'Activa',
    vencida: 'Vencida',
    inactiva: 'Inactiva',
};
const ESTADO_TONO = {
    activa: 'success',
    vencida: 'warning',
    inactiva: 'neutral',
};
function hoyIso() {
    return new Date().toISOString().slice(0, 10);
}
function estadoDe(r) {
    if (!r.activo)
        return 'inactiva';
    if (r.vigenteHasta && r.vigenteHasta < hoyIso())
        return 'vencida';
    return 'activa';
}
function formatearPct(pct) {
    if (pct == null)
        return '—';
    const n = typeof pct === 'string' ? Number(pct) : pct;
    if (!Number.isFinite(n))
        return '—';
    return Number.isInteger(n) ? `${n}%` : `${n.toString().replace('.', ',')}%`;
}
function singular(n, sing, plural) {
    return n === 1 ? sing : plural;
}
function tieneRestricciones(r) {
    return Boolean((r.soloTiposClase && r.soloTiposClase.length) ||
        (r.soloMetodosPago && r.soloMetodosPago.length) ||
        (r.montoMinimo != null && Number(r.montoMinimo) > 0));
}
export function DescuentosPage() {
    const { tieneRol } = useSession();
    const puedeAdmin = tieneRol('ROLE_ADMINISTRAR_DESCUENTO');
    const toast = useToast();
    const [tipo, setTipo] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('activa');
    const [busqueda, setBusqueda] = useState('');
    const activoParam = filtroEstado === 'inactiva' ? false : filtroEstado === 'todas' ? undefined : true;
    const { data, isLoading } = useReglasDescuento(tipo || undefined, activoParam);
    const desactivar = useDesactivarReglaDescuento();
    const [formOpen, setFormOpen] = useState(false);
    const [editable, setEditable] = useState(null);
    const [desactivando, setDesactivando] = useState(null);
    const reglasFiltradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        return (data ?? []).filter((r) => {
            if (q) {
                const enNombre = r.nombre.toLowerCase().includes(q);
                const enCodigo = r.codigo.toLowerCase().includes(q);
                if (!enNombre && !enCodigo)
                    return false;
            }
            if (filtroEstado !== 'todas' && estadoDe(r) !== filtroEstado)
                return false;
            return true;
        });
    }, [data, busqueda, filtroEstado]);
    const totales = useMemo(() => {
        const base = data ?? [];
        return {
            total: base.length,
            activas: base.filter((r) => estadoDe(r) === 'activa').length,
            vencidas: base.filter((r) => estadoDe(r) === 'vencida').length,
            inactivas: base.filter((r) => estadoDe(r) === 'inactiva').length,
        };
    }, [data]);
    const columnas = useMemo(() => [
        {
            key: 'codigo',
            header: 'Código',
            width: '140px',
            cell: (r) => (_jsx("span", { className: "font-mono text-xs font-semibold uppercase text-text-secondary", children: r.codigo })),
        },
        {
            key: 'nombre',
            header: 'Regla',
            cell: (r) => (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "font-medium", children: r.nombre }), r.descripcion ? (_jsx("span", { className: "text-[10px] text-text-tertiary", children: r.descripcion })) : null, tieneRestricciones(r) ? _jsx(RestriccionesChips, { regla: r }) : null] })),
        },
        {
            key: 'tipo',
            header: 'Tipo',
            cell: (r) => (_jsx(Chip, { tono: TIPO_TONO[r.tipo] ?? 'neutral', children: TIPO_LABEL[r.tipo] ?? r.tipo })),
            width: '120px',
        },
        {
            key: 'valor',
            header: 'Valor',
            align: 'right',
            width: '120px',
            cell: (r) => r.pct != null ? (_jsx("span", { className: "num font-semibold", children: formatearPct(r.pct) })) : r.montoFijo != null ? (_jsx("span", { className: "num font-semibold", children: formatearMonto(Number(r.montoFijo)) })) : ('—'),
        },
        {
            key: 'vigencia',
            header: 'Vigencia',
            cell: (r) => (_jsxs("span", { className: "text-xs", children: [r.vigenteDesde ? formatearFecha(r.vigenteDesde) : '—', r.vigenteHasta ? ` → ${formatearFecha(r.vigenteHasta)}` : ' →'] })),
            width: '180px',
        },
        {
            key: 'estado',
            header: 'Estado',
            width: '100px',
            cell: (r) => {
                const estado = estadoDe(r);
                return _jsx(Chip, { tono: ESTADO_TONO[estado], children: ESTADO_LABEL[estado] });
            },
        },
        ...(puedeAdmin
            ? [
                {
                    key: 'acc',
                    header: '',
                    width: '110px',
                    align: 'right',
                    cell: (r) => (_jsxs("div", { className: "flex items-center justify-end gap-1", children: [_jsx(Button, { variant: "ghost", size: "icon", onClick: () => {
                                    setEditable(r);
                                    setFormOpen(true);
                                }, "aria-label": "Editar", children: _jsx(Pencil, { className: "h-3.5 w-3.5" }) }), r.activo ? (_jsx(Button, { variant: "ghost", size: "icon", onClick: () => setDesactivando(r), "aria-label": "Desactivar", className: "text-danger", children: _jsx(Trash2, { className: "h-3.5 w-3.5" }) })) : null] })),
                },
            ]
            : []),
    ], [puedeAdmin]);
    const ejecutarDesactivar = () => {
        if (!desactivando)
            return;
        desactivar.mutate(desactivando.id, {
            onSuccess: () => {
                toast.exito('Regla desactivada');
                setDesactivando(null);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos desactivarla.'),
        });
    };
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-text-primary", children: "Reglas de descuento" }), _jsx("p", { className: "text-xs text-text-secondary", children: "Reglas reutilizables que se pueden aplicar a reservas (banco, escuela, promoci\u00F3n)." })] }), puedeAdmin ? (_jsxs(Button, { onClick: () => {
                            setEditable(null);
                            setFormOpen(true);
                        }, children: [_jsx(Plus, { className: "h-4 w-4" }), "Nueva regla"] })) : null] }), _jsxs("div", { className: "flex flex-wrap items-end gap-2", children: [_jsxs("div", { className: "relative flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Buscar" }), _jsx(Input, { type: "search", value: busqueda, onChange: (e) => setBusqueda(e.target.value), placeholder: "C\u00F3digo o nombre\u2026", className: "w-56 pl-8" }), _jsx(Search, { className: "pointer-events-none absolute bottom-2.5 left-2 h-4 w-4 text-text-tertiary" })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Tipo" }), _jsxs(Select, { value: tipo, onChange: (e) => setTipo(e.target.value), className: "w-44", children: [_jsx("option", { value: "", children: "Todos los tipos" }), TIPOS_DESCUENTO.map((t) => (_jsx("option", { value: t, children: TIPO_LABEL[t] ?? t }, t)))] })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Estado" }), _jsxs(Select, { value: filtroEstado, onChange: (e) => setFiltroEstado(e.target.value), className: "w-36", children: [_jsx("option", { value: "todas", children: "Todas" }), _jsx("option", { value: "activa", children: "Activas" }), _jsx("option", { value: "vencida", children: "Vencidas" }), _jsx("option", { value: "inactiva", children: "Inactivas" })] })] })] }), _jsxs("p", { className: "text-xs text-text-tertiary", children: [_jsxs("span", { className: "font-medium text-text-secondary", children: [reglasFiltradas.length, reglasFiltradas.length !== totales.total ? ` / ${totales.total}` : '', ' ', singular(reglasFiltradas.length, 'regla', 'reglas')] }), ' — ', totales.activas, " ", singular(totales.activas, 'activa', 'activas'), filtroEstado === 'inactiva'
                        ? ` · ${totales.inactivas} ${singular(totales.inactivas, 'inactiva', 'inactivas')}`
                        : ` · ${totales.vencidas} ${singular(totales.vencidas, 'vencida', 'vencidas')}`] }), _jsx(Table, { columns: columnas, items: reglasFiltradas, rowKey: (r) => r.id, cargando: isLoading, vacioTitulo: (data?.length ?? 0) > 0 ? 'Sin coincidencias' : 'Sin reglas', vacioDescripcion: (data?.length ?? 0) > 0
                    ? 'Probá ajustar los filtros o la búsqueda.'
                    : 'Creá la primera regla de descuento.', vacioAccion: puedeAdmin && (data?.length ?? 0) === 0 ? (_jsxs(Button, { onClick: () => {
                        setEditable(null);
                        setFormOpen(true);
                    }, children: [_jsx(Percent, { className: "h-4 w-4" }), "Crear"] })) : null }), _jsx(DescuentoReglaDialog, { open: formOpen, onOpenChange: (open) => {
                    setFormOpen(open);
                    if (!open)
                        setEditable(null);
                }, regla: editable }), _jsx(ConfirmDialog, { open: Boolean(desactivando), onOpenChange: (open) => !open && setDesactivando(null), titulo: "Desactivar regla", descripcion: desactivando
                    ? `"${desactivando.nombre}" se mantiene en reservas que ya la usaron, pero no aparecerá al crear reservas nuevas.`
                    : undefined, textoConfirmar: "Desactivar", destructivo: true, cargando: desactivar.isPending, onConfirmar: ejecutarDesactivar })] }));
}
function RestriccionesChips({ regla }) {
    const tiposClase = regla.soloTiposClase ?? [];
    const metodos = regla.soloMetodosPago ?? [];
    const minimo = regla.montoMinimo != null ? Number(regla.montoMinimo) : 0;
    return (_jsxs("div", { className: "flex flex-wrap items-center gap-1", children: [tiposClase.length > 0 ? (_jsxs("span", { className: "inline-flex items-center gap-1 rounded bg-border-subtle px-1.5 py-0.5 text-[10px] text-text-secondary", title: `Solo aplica a: ${tiposClase.map((t) => TIPO_CLASE_LABEL[t] ?? t).join(', ')}`, children: [_jsx(GraduationCap, { className: "h-3 w-3" }), tiposClase.length === 1
                        ? TIPO_CLASE_LABEL[tiposClase[0]] ?? tiposClase[0]
                        : `${tiposClase.length} tipos`] })) : null, metodos.length > 0 ? (_jsxs("span", { className: "inline-flex items-center gap-1 rounded bg-border-subtle px-1.5 py-0.5 text-[10px] text-text-secondary", title: `Solo con: ${metodos.map((m) => METODO_PAGO_LABEL[m] ?? m).join(', ')}`, children: [_jsx(CreditCard, { className: "h-3 w-3" }), metodos.length === 1
                        ? METODO_PAGO_LABEL[metodos[0]] ?? metodos[0]
                        : `${metodos.length} métodos`] })) : null, minimo > 0 ? (_jsxs("span", { className: "inline-flex items-center gap-1 rounded bg-border-subtle px-1.5 py-0.5 text-[10px] text-text-secondary", title: `Solo si el total es mayor o igual a ${formatearMonto(minimo)}`, children: [_jsx(Wallet, { className: "h-3 w-3" }), "M\u00EDn ", formatearMonto(minimo)] })) : null] }));
}
function DescuentoReglaDialog({ open, onOpenChange, regla, }) {
    const toast = useToast();
    const crear = useCrearReglaDescuento();
    const editar = useEditarReglaDescuento();
    const modoEdicion = Boolean(regla);
    const isPending = crear.isPending || editar.isPending;
    const form = useForm({
        resolver: zodResolver(DescuentoReglaFormSchema),
        defaultValues: {
            tipo: 'ESCUELA',
            codigo: '',
            nombre: '',
            descripcion: '',
            tipoValor: 'PCT',
            valor: '',
            vigenteDesde: '',
            vigenteHasta: '',
            soloTiposClase: [],
            soloMetodosPago: [],
            montoMinimo: '',
        },
    });
    useEffect(() => {
        if (!open)
            return;
        if (regla) {
            form.reset({
                tipo: regla.tipo ?? 'ESCUELA',
                codigo: regla.codigo,
                nombre: regla.nombre,
                descripcion: regla.descripcion ?? '',
                tipoValor: regla.pct != null ? 'PCT' : 'MONTO_FIJO',
                valor: regla.pct != null ? String(regla.pct) : String(regla.montoFijo ?? ''),
                vigenteDesde: regla.vigenteDesde ?? '',
                vigenteHasta: regla.vigenteHasta ?? '',
                soloTiposClase: regla.soloTiposClase ?? [],
                soloMetodosPago: regla.soloMetodosPago ?? [],
                montoMinimo: regla.montoMinimo != null ? String(regla.montoMinimo) : '',
            });
        }
        else {
            form.reset({
                tipo: 'ESCUELA',
                codigo: '',
                nombre: '',
                descripcion: '',
                tipoValor: 'PCT',
                valor: '',
                vigenteDesde: '',
                vigenteHasta: '',
                soloTiposClase: [],
                soloMetodosPago: [],
                montoMinimo: '',
            });
        }
    }, [open, regla, form]);
    const [tab, setTab] = useState('datos');
    useEffect(() => {
        if (open)
            setTab('datos');
    }, [open]);
    const tipoValor = form.watch('tipoValor');
    const tiposClaseSel = form.watch('soloTiposClase');
    const metodosSel = form.watch('soloMetodosPago');
    const toggleTipoClase = (c) => {
        const sel = new Set(tiposClaseSel);
        if (sel.has(c))
            sel.delete(c);
        else
            sel.add(c);
        form.setValue('soloTiposClase', Array.from(sel), { shouldDirty: true });
    };
    const toggleMetodo = (m) => {
        const sel = new Set(metodosSel);
        if (sel.has(m))
            sel.delete(m);
        else
            sel.add(m);
        form.setValue('soloMetodosPago', Array.from(sel), { shouldDirty: true });
    };
    const errors = form.formState.errors;
    const errorEnDatos = Boolean(errors.tipo ||
        errors.codigo ||
        errors.nombre ||
        errors.descripcion ||
        errors.tipoValor ||
        errors.valor ||
        errors.vigenteDesde ||
        errors.vigenteHasta);
    const errorEnRestricciones = Boolean(errors.soloTiposClase || errors.soloMetodosPago || errors.montoMinimo);
    const submitForm = form.handleSubmit((values) => {
        const onSuccess = () => {
            toast.exito(modoEdicion ? 'Regla actualizada' : 'Regla creada');
            onOpenChange(false);
        };
        const onError = (err) => {
            if (err instanceof AppError) {
                if (err.codigo === 'CODIGO_DUPLICADO') {
                    form.setError('codigo', { message: 'Ya existe una regla con ese código.' });
                    setTab('datos');
                    return;
                }
                if (err.codigo === 'TIPO_CLASE_INVALIDO') {
                    form.setError('soloTiposClase', { message: 'Tipo de clase inválido.' });
                    setTab('restricciones');
                    return;
                }
                if (err.codigo === 'METODO_PAGO_INVALIDO') {
                    form.setError('soloMetodosPago', { message: 'Método de pago inválido.' });
                    setTab('restricciones');
                    return;
                }
            }
            toast.error(err instanceof AppError ? err.mensaje : 'No pudimos guardar.');
        };
        if (modoEdicion && regla) {
            editar.mutate({ id: regla.id, values }, { onSuccess, onError });
            return;
        }
        crear.mutate(values, { onSuccess, onError });
    }, () => {
        // Hubo errores de validación local: saltar al tab que tiene el primero
        const errs = form.formState.errors;
        if (errs.soloTiposClase || errs.soloMetodosPago || errs.montoMinimo) {
            if (!(errs.tipo || errs.codigo || errs.nombre || errs.valor)) {
                setTab('restricciones');
                return;
            }
        }
        setTab('datos');
    });
    return (_jsx(Modal, { open: open, onOpenChange: onOpenChange, children: _jsxs(ModalContent, { size: "lg", children: [_jsx(ModalHeader, { titulo: modoEdicion ? 'Editar regla de descuento' : 'Nueva regla de descuento' }), _jsxs("form", { onSubmit: submitForm, noValidate: true, children: [_jsx(ModalBody, { children: _jsxs(Tabs.Root, { value: tab, onValueChange: (v) => setTab(v), children: [_jsxs(Tabs.List, { className: "mb-3 flex gap-1 border-b border-border-subtle", children: [_jsx(TabTrigger, { value: "datos", mostrarError: errorEnDatos && tab !== 'datos', children: "Datos" }), _jsxs(TabTrigger, { value: "restricciones", mostrarError: errorEnRestricciones && tab !== 'restricciones', children: ["Restricciones ", _jsx("span", { className: "text-text-tertiary", children: "(opcional)" })] })] }), _jsx(Tabs.Content, { value: "datos", children: _jsxs("div", { className: "grid grid-cols-1 gap-2.5 sm:grid-cols-2", children: [_jsx(Field, { label: "Tipo", error: errors.tipo?.message, required: true, children: _jsx(Select, { disabled: modoEdicion, ...form.register('tipo'), children: TIPOS_DESCUENTO.map((t) => (_jsx("option", { value: t, children: TIPO_LABEL[t] ?? t }, t))) }) }), _jsx(Field, { label: "C\u00F3digo", error: errors.codigo?.message, helper: "Lo que se tipea al aplicar el descuento. Ej. BANCO10, VERANO25.", required: true, children: _jsx(Input, { autoFocus: true, className: "font-mono uppercase", placeholder: "EJ. BANCO10", ...form.register('codigo', {
                                                            onChange: (e) => {
                                                                e.target.value = String(e.target.value).toUpperCase();
                                                            },
                                                        }) }) }), _jsx(Field, { label: "Nombre", error: errors.nombre?.message, className: "sm:col-span-2", required: true, children: _jsx(Input, { ...form.register('nombre') }) }), _jsx(Field, { label: "Descripci\u00F3n", error: errors.descripcion?.message, className: "sm:col-span-2", children: _jsx(Textarea, { rows: 1, ...form.register('descripcion') }) }), _jsx(Field, { label: "Tipo de valor", error: errors.tipoValor?.message, required: true, children: _jsxs(Select, { disabled: modoEdicion, ...form.register('tipoValor'), children: [_jsx("option", { value: "PCT", children: "Porcentaje" }), _jsx("option", { value: "MONTO_FIJO", children: "Monto fijo" })] }) }), _jsx(Field, { label: tipoValor === 'PCT' ? 'Porcentaje' : 'Monto', error: errors.valor?.message, required: true, children: tipoValor === 'PCT' ? (_jsx(Input, { inputMode: "decimal", placeholder: "15", disabled: modoEdicion, ...form.register('valor') })) : (_jsx(MoneyInput, { value: form.watch('valor') ?? '', onChange: (v) => form.setValue('valor', v, { shouldDirty: true, shouldValidate: true }), disabled: modoEdicion })) }), _jsx(Field, { label: "Vigente desde", error: errors.vigenteDesde?.message, children: _jsx(Input, { type: "date", disabled: modoEdicion, ...form.register('vigenteDesde') }) }), _jsx(Field, { label: "Vigente hasta", error: errors.vigenteHasta?.message, children: _jsx(Input, { type: "date", disabled: modoEdicion, ...form.register('vigenteHasta') }) })] }) }), _jsxs(Tabs.Content, { value: "restricciones", children: [_jsx("p", { className: "mb-3 text-xs text-text-tertiary", children: "Las restricciones son opcionales. Si dej\u00E1s todo vac\u00EDo, la regla aplica a cualquier reserva." }), _jsxs("div", { className: "grid grid-cols-1 gap-2.5 sm:grid-cols-2", children: [_jsx(Field, { label: "Solo para tipos de clase", error: errors.soloTiposClase?.message, children: _jsx("div", { className: "flex flex-wrap gap-1.5", children: TIPOS_CLASE_DISPONIBLES.map((c) => {
                                                                const sel = tiposClaseSel.includes(c);
                                                                return (_jsx("button", { type: "button", onClick: () => toggleTipoClase(c), className: `rounded-md border px-2.5 py-1 text-xs transition ${sel
                                                                        ? 'border-primary bg-primary-light text-primary'
                                                                        : 'border-border bg-surface text-text-secondary hover:border-border-strong'}`, children: TIPO_CLASE_LABEL[c] ?? c }, c));
                                                            }) }) }), _jsx(Field, { label: "Solo con m\u00E9todos de pago", error: errors.soloMetodosPago?.message, children: _jsx("div", { className: "flex flex-wrap gap-1.5", children: METODOS_PAGO_RESTRICCION.map((m) => {
                                                                const sel = metodosSel.includes(m);
                                                                return (_jsx("button", { type: "button", onClick: () => toggleMetodo(m), className: `rounded-md border px-2.5 py-1 text-xs transition ${sel
                                                                        ? 'border-primary bg-primary-light text-primary'
                                                                        : 'border-border bg-surface text-text-secondary hover:border-border-strong'}`, children: METODO_PAGO_LABEL[m] ?? m }, m));
                                                            }) }) }), _jsx(Field, { label: "Monto m\u00EDnimo del total", error: errors.montoMinimo?.message, helper: "La regla solo aplica si el total de la reserva alcanza este monto.", className: "sm:col-span-2", children: _jsx(MoneyInput, { value: form.watch('montoMinimo') ?? '', onChange: (v) => form.setValue('montoMinimo', v, { shouldDirty: true }) }) })] })] })] }) }), _jsxs(ModalFooter, { children: [_jsx(Button, { type: "button", variant: "secondary", onClick: () => onOpenChange(false), disabled: isPending, children: "Cancelar" }), _jsx(Button, { type: "submit", cargando: isPending, children: modoEdicion ? 'Guardar' : 'Crear' })] })] })] }) }));
}
function TabTrigger({ value, mostrarError, children, }) {
    return (_jsxs(Tabs.Trigger, { value: value, className: cn('relative px-3 py-2 text-sm font-medium text-text-secondary transition', 'data-[state=active]:text-primary', 'after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-transparent data-[state=active]:after:bg-primary'), children: [children, mostrarError ? (_jsx("span", { className: "ml-2 inline-block h-1.5 w-1.5 rounded-full bg-danger align-middle" })) : null] }));
}
